import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatMoney, formatDate } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CaretLeft, WhatsappLogo, Envelope, Phone, MapPin, Handshake, Scales, Warning, PaperPlaneTilt, Plus, Buildings, Coins, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Attachments from '@/components/Attachments';
import ReferToLegalButton from '@/components/ReferToLegalButton';

const ACTION_TYPES = [
  { value: 'call', label: 'اتصال هاتفي', icon: Phone },
  { value: 'whatsapp', label: 'واتساب', icon: WhatsappLogo },
  { value: 'email', label: 'بريد إلكتروني', icon: Envelope },
  { value: 'visit', label: 'زيارة ميدانية', icon: MapPin },
  { value: 'meeting', label: 'اجتماع', icon: Handshake },
  { value: 'escalate_mgmt', label: 'رفع للإدارة', icon: Warning },
  { value: 'legal', label: 'إحالة قانونية', icon: Scales },
  { value: 'financial', label: 'إحالة مالية', icon: Buildings },
  { value: 'waiting', label: 'انتظار رد', icon: PaperPlaneTilt },
  { value: 'other', label: 'أخرى', icon: Plus },
];

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = (s === 'fully paid' || s === 'paid')
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : (s === 'partially paid' || s === 'partial') ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';
  const label = (s === 'fully paid' || s === 'paid') ? 'مدفوعة بالكامل'
    : (s === 'partially paid' || s === 'partial') ? 'مدفوعة جزئياً'
    : s === 'unpaid' ? 'غير مدفوعة' : status;
  return <Badge className={`${cls} border hover:opacity-90`}>{label}</Badge>;
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ action_type: 'call', description: '', result: '', status: 'done', next_followup: '' });
  const [waMessage, setWaMessage] = useState('');
  const [waOpen, setWaOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', deposit_date: '', receipt_no: '', receipt_date: '', notes: '' });

  const load = () => {
    setLoading(true);
    api.get(`/invoices/${id}`).then(({ data }) => setInv(data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/message-templates', { params: { channel: 'whatsapp' } }).then(({ data }) => setTemplates(data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPayments = () => {
    api.get(`/invoices/${id}/payments`).then(({ data }) => setPayments(data)).catch(() => {});
  };
  useEffect(() => { loadPayments(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { toast.error('أدخل مبلغاً صحيحاً'); return; }
    try {
      await api.post(`/invoices/${id}/payments`, {
        amount,
        deposit_date: payForm.deposit_date || null,
        receipt_no: payForm.receipt_no || null,
        receipt_date: payForm.receipt_date || null,
        notes: payForm.notes || null,
      });
      toast.success('تم تسجيل السداد');
      setPayOpen(false);
      setPayForm({ amount: '', deposit_date: '', receipt_no: '', receipt_date: '', notes: '' });
      load();
      loadPayments();
    } catch (err) {
      toast.error('فشل: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm('حذف عملية السداد؟')) return;
    try {
      await api.delete(`/payments/${pid}`);
      toast.success('تم الحذف');
      load(); loadPayments();
    } catch { toast.error('فشل الحذف'); }
  };

  const applyTemplate = async (tid) => {
    setSelectedTemplate(tid);
    if (!tid) { setWaMessage(''); return; }
    try {
      const { data } = await api.post(`/message-templates/${tid}/render`, { invoice_id: id });
      setWaMessage(data.body || '');
    } catch { toast.error('فشل تحميل القالب'); }
  };

  const addEntry = async () => {
    if (!form.description) { toast.error('أدخل وصفاً للإجراء'); return; }
    try {
      await api.post(`/invoices/${id}/timeline`, form);
      toast.success('تم تسجيل الإجراء');
      setOpen(false);
      setForm({ action_type: 'call', description: '', result: '', status: 'done', next_followup: '' });
      load();
    } catch (err) {
      toast.error('فشل الحفظ');
    }
  };

  const sendWhatsapp = () => {
    if (!inv.phone) { toast.error('لا يوجد رقم جوال'); return; }
    const cleanPhone = String(inv.phone).replace(/\D/g, '');
    const msg = waMessage || `عزيزنا ${inv.tenant_name || ''}،\nنود تذكيركم بالفاتورة رقم ${inv.payment_no} بمبلغ ${formatMoney(inv.remaining_amount)} ريال المستحقة بتاريخ ${formatDate(inv.due_date)}.\nشكراً لتعاونكم.`;
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    api.post(`/invoices/${id}/timeline`, { action_type: 'whatsapp', description: 'إرسال رسالة واتساب', result: msg }).then(load);
    setWaOpen(false);
  };

  if (loading || !inv) return <div className="text-slate-500 text-sm">جاري التحميل...</div>;

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="back-button">
          <CaretLeft className="ms-1" /> رجوع
        </Button>
        {inv.referred_to_legal ? (
          <div className="flex items-center gap-2 text-sm text-[#1E3A5F] bg-[#1E3A5F]/10 px-3 py-1.5 rounded-md" data-testid="referred-badge">
            <Scales size={16} weight="duotone" />
            <span>محال للقانونية: <span className="font-mono font-semibold">{inv.legal_claim_no}</span></span>
          </div>
        ) : (
          <ReferToLegalButton
            invoice_id={inv.id}
            contract_no={inv.contract_no}
            contextLabel={`الفاتورة ${inv.payment_no}`}
            onDone={() => window.location.reload()}
          />
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6 border border-slate-200 rounded-md shadow-none">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Invoice · Yardi</div>
                <div className="text-2xl font-bold text-slate-900 mono">#{inv.payment_no}</div>
                <div className="text-sm text-slate-500 mt-1">{inv.property_name} — {inv.tenant_name}</div>
              </div>
              <StatusBadge status={inv.payment_status} />
            </div>

            <div className="grid grid-cols-3 gap-4 pb-4 border-b border-slate-200">
              <div>
                <div className="text-xs text-slate-500 mb-1">إجمالي المبلغ</div>
                <div className="text-xl font-bold tabular-nums">{formatMoney(inv.invoice_amount)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">المحصل</div>
                <div className="text-xl font-bold tabular-nums text-emerald-600">{formatMoney(inv.collected_amount)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">المتبقي</div>
                <div className="text-xl font-bold tabular-nums text-red-600">{formatMoney(inv.remaining_amount)}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 pt-4 text-sm">
              <Row label="رقم العقد" value={inv.contract_no} />
              <Row label="رقم الأصل" value={inv.asset_no} />
              <Row label="الفرع" value={inv.branch} />
              <Row label="المدينة" value={inv.city} />
              <Row label="الفترة من" value={formatDate(inv.period_from)} />
              <Row label="الفترة إلى" value={formatDate(inv.period_to)} />
              <Row label="تاريخ الاستحقاق" value={formatDate(inv.due_date)} />
              <Row label="نوع الفاتورة" value={inv.invoice_type} />
              <Row label="الجوال" value={inv.phone} />
              <Row label="البريد" value={inv.email} />
            </div>
          </Card>

          {/* Manual Payments */}
          <Card className="p-6 border border-slate-200 rounded-md shadow-none">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#B4935A]">Manual Payments</div>
                <div className="text-lg font-semibold text-slate-900">سجل السداد اليدوي</div>
              </div>
              <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 text-white hover:bg-emerald-700" size="sm" data-testid="add-payment-button">
                    <Coins className="ms-1" size={16} /> إضافة سداد
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>تسجيل سداد يدوي</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>المبلغ المحصل *</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                    <div><Label>تاريخ الإيداع</Label><Input type="date" value={payForm.deposit_date} onChange={(e) => setPayForm({ ...payForm, deposit_date: e.target.value })} data-testid="payment-deposit-date" /></div>
                    <div><Label>رقم سند القبض</Label><Input value={payForm.receipt_no} onChange={(e) => setPayForm({ ...payForm, receipt_no: e.target.value })} data-testid="payment-receipt-no" /></div>
                    <div><Label>تاريخ سند القبض</Label><Input type="date" value={payForm.receipt_date} onChange={(e) => setPayForm({ ...payForm, receipt_date: e.target.value })} /></div>
                    <div><Label>ملاحظات</Label><Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
                    <Button onClick={addPayment} className="w-full bg-slate-900 text-white" data-testid="save-payment-button">حفظ السداد</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {payments.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6 border border-dashed border-slate-200 rounded-md">
                لم يُسجَّل سداد يدوي بعد. اضغط "إضافة سداد" لتسجيل عملية السداد وتحديث المبلغ المتبقي وحالة الفاتورة تلقائيًا.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-end p-2 font-medium">المبلغ</th>
                      <th className="text-start p-2 font-medium">تاريخ الإيداع</th>
                      <th className="text-start p-2 font-medium">رقم السند</th>
                      <th className="text-start p-2 font-medium">تاريخ السند</th>
                      <th className="text-start p-2 font-medium">مسجّل بواسطة</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="p-2 text-end tabular-nums font-medium text-emerald-700">{formatMoney(p.amount)}</td>
                        <td className="p-2 mono text-slate-600">{formatDate(p.deposit_date)}</td>
                        <td className="p-2 mono">{p.receipt_no || '—'}</td>
                        <td className="p-2 mono text-slate-600">{formatDate(p.receipt_date)}</td>
                        <td className="p-2 text-slate-600">{p.recorded_by_name}</td>
                        <td className="p-2"><Button size="icon" variant="ghost" onClick={() => deletePayment(p.id)}><Trash size={14} className="text-red-600" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>


          {/* Timeline */}
          <Card className="p-6 border border-slate-200 rounded-md shadow-none">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#B4935A]">Timeline</div>
                <div className="text-lg font-semibold text-slate-900">سجل إجراءات التحصيل</div>
              </div>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid="add-timeline-button">
                    <Plus className="ms-1" size={16} /> إجراء جديد
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>تسجيل إجراء متابعة</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>نوع الإجراء</Label>
                      <Select value={form.action_type} onValueChange={(v) => setForm({ ...form, action_type: v })}>
                        <SelectTrigger data-testid="action-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>الوصف</Label>
                      <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="action-description-input" />
                    </div>
                    <div>
                      <Label>النتيجة</Label>
                      <Input value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} />
                    </div>
                    <div>
                      <Label>المتابعة القادمة</Label>
                      <Input type="date" value={form.next_followup} onChange={(e) => setForm({ ...form, next_followup: e.target.value })} />
                    </div>
                    <Button onClick={addEntry} className="w-full bg-slate-900 text-white" data-testid="save-timeline-button">حفظ</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {inv.timeline?.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                لا يوجد سجل متابعة. أضف أول إجراء.
              </div>
            ) : (
              <div>
                {inv.timeline?.map((t) => {
                  const at = ACTION_TYPES.find(a => a.value === t.action_type) || ACTION_TYPES[9];
                  const Icon = at.icon;
                  return (
                    <div key={t.id} className="timeline-item">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Icon size={16} className="text-[#B4935A]" />
                          <div className="font-semibold text-sm text-slate-900">{at.label}</div>
                        </div>
                        <div className="text-xs text-slate-500 mono">{new Date(t.created_at).toLocaleString('ar-SA')}</div>
                      </div>
                      {t.description && <div className="text-sm text-slate-700 mb-1">{t.description}</div>}
                      {t.result && <div className="text-xs text-slate-500">النتيجة: {t.result}</div>}
                      {t.next_followup && <div className="text-xs text-amber-700 mt-1">متابعة: {t.next_followup}</div>}
                      <div className="text-xs text-slate-400 mt-1">بواسطة: {t.user_name}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-3">Quick Actions</div>
            <div className="space-y-2">
              <Dialog open={waOpen} onOpenChange={setWaOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="whatsapp-button">
                    <WhatsappLogo className="ms-2" size={18} /> إرسال واتساب
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>رسالة واتساب</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    {templates.length > 0 && (
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">اختر قالباً جاهزاً</label>
                        <select
                          value={selectedTemplate}
                          onChange={(e) => applyTemplate(e.target.value)}
                          className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                          data-testid="wa-template-select"
                        >
                          <option value="">— بدون قالب —</option>
                          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    )}
                    <Textarea rows={7} value={waMessage} onChange={(e) => setWaMessage(e.target.value)} placeholder={`عزيزنا ${inv.tenant_name || ''}، نود تذكيركم بالفاتورة...`} />
                    <Button onClick={sendWhatsapp} className="w-full bg-emerald-600 text-white">فتح واتساب</Button>
                  </div>
                </DialogContent>
              </Dialog>
              {inv.phone && (
                <Button variant="outline" className="w-full" asChild>
                  <a href={`tel:${inv.phone}`}><Phone className="ms-2" size={18} /> اتصال {inv.phone}</a>
                </Button>
              )}
              {inv.email && (
                <Button variant="outline" className="w-full" asChild>
                  <a href={`mailto:${inv.email}`}><Envelope className="ms-2" size={18} /> بريد إلكتروني</a>
                </Button>
              )}
            </div>
          </Card>

          {inv.extra && Object.keys(inv.extra).length > 0 && (
            <Card className="p-5 border border-slate-200 rounded-md shadow-none">
              <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-3">Extra Fields (Yardi)</div>
              <div className="space-y-2 text-xs">
                {Object.entries(inv.extra).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 pb-1">
                    <span className="text-slate-500">{k}</span>
                    <span className="text-slate-700 mono">{String(v)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            <Attachments entityType="invoice" entityId={inv.id} />
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800 mono">{value || '—'}</div>
  </div>
);
